import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import type {Dispatch, SetStateAction} from 'react';

import {ProcessFlowchartEditorConfigurator} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/ProcessFlowchartEditorConfigurator';
import {FlowchartEditorMode} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/ProcessFlowchartEditorModeConstants';
import {assertNever} from 'util/Types';

import {refreshNodeDisplayNames} from '../processFlowchartConfigEditorUtils';
import {FlowchartConfigWithMetadata} from '../redux/flowchartConfigAutosaveManagerCallbacks';
import {
  createInitialExecutionDraftState,
  mergeStatusCountsIntoExecutionDraft,
} from './createInitialExecutionDraftState';
import {useExecutionFlowchartCore} from './executionFlowchartCore';
import type {LineageDataResult, LoadedFlowchartData} from './executionFlowchartData';
import {LineageDataResultType, useProcessData, useTaskGroupLineageData} from './executionFlowchartData';
import type {ExecutionFlowchartReadinessSnapshot} from './ExecutionFlowchartPaneTypes';
import {
  getExecutionFlowchartIdFromProcess,
  getFlowchartConfigIdFromProcess,
} from './executionFlowchartProcessIds';

const EMPTY_LINEAGE_NODE_TASK_SCHEMA_IDS: Array<string> = [];

/** Fills or updates the execution draft once template data, configurator, and lineage are ready. */
function useBootstrapExecutionDraft(args: {
  data: LoadedFlowchartData | null;
  executionEditorConfiguratorBase: ProcessFlowchartEditorConfigurator | null;
  setDraftState: Dispatch<SetStateAction<FlowchartConfigWithMetadata | null>>;
  lineageResult: LineageDataResult;
}): void {
  const {data, executionEditorConfiguratorBase, setDraftState, lineageResult} = args;

  const lineageResultType = lineageResult.resultType;
  const lineageStatusCounts =
    lineageResultType === LineageDataResultType.SUCCESS ? lineageResult.data : undefined;

  useEffect(() => {
    if (data == null || executionEditorConfiguratorBase == null) {
      return;
    }
    if (lineageResultType === LineageDataResultType.PENDING) {
      return;
    }

    setDraftState((prev) => {
      if (prev == null) {
        return createInitialExecutionDraftState(
          data,
          executionEditorConfiguratorBase.flowchartTypeName,
          lineageStatusCounts
        );
      }
      if (lineageStatusCounts != null && Object.keys(lineageStatusCounts).length > 0) {
        const merged = mergeStatusCountsIntoExecutionDraft(prev, lineageStatusCounts);
        return merged === prev ? prev : merged;
      }
      return prev;
    });
  }, [data, executionEditorConfiguratorBase, lineageResultType, lineageStatusCounts, setDraftState]);
}

interface UseExecutionFlowchartDraftStateResult {
  draftState: FlowchartConfigWithMetadata | null;
  setDraftState: Dispatch<SetStateAction<FlowchartConfigWithMetadata | null>>;
  clearDraft: () => void;
  readinessSnapshot: ExecutionFlowchartReadinessSnapshot;
}

type UseExecutionFlowchartDraftStateParams =
  | {
      resolution: 'flowchartConfig';
      flowchartConfigId: string | null;
      editorMode: FlowchartEditorMode;
    }
  | {
      resolution: 'process';
      processId: string;
      editorMode: FlowchartEditorMode;
    };

interface DraftResolutionDerived {
  flowchartConfigId: string | null;
  executionFlowchartId: string | null;
  externalInaccessible: boolean;
  externalPending: boolean;
}

/** Derives draft state from flowchart config or process, with external access checks. */
function useExecutionFlowchartDraftState(
  params: UseExecutionFlowchartDraftStateParams
): UseExecutionFlowchartDraftStateResult {
  const processIdForQuery = params.resolution === 'process' ? params.processId : null;
  const processData = useProcessData(processIdForQuery);

  const derived = useMemo((): DraftResolutionDerived => {
    switch (params.resolution) {
      case 'flowchartConfig':
        return {
          flowchartConfigId: params.flowchartConfigId,
          executionFlowchartId: null,
          externalInaccessible: false,
          externalPending: false,
        };
      case 'process':
        return {
          flowchartConfigId: getFlowchartConfigIdFromProcess(processData),
          executionFlowchartId: getExecutionFlowchartIdFromProcess(processData),
          externalInaccessible:
            processData !== undefined &&
            (processData == null || getFlowchartConfigIdFromProcess(processData) == null),
          externalPending: processData === undefined,
        };
      default:
        return assertNever(params);
    }
  }, [params, processData]);

  const [draftState, setDraftState] = useState<FlowchartConfigWithMetadata | null>(null);
  const skipFlowchartTypeConfigurator =
    params.resolution === 'flowchartConfig' && params.flowchartConfigId == null;
  const {isConfiguratorLoadError, executionEditorConfigurator, dataResult, data} = useExecutionFlowchartCore({
    flowchartConfigId: derived.flowchartConfigId,
    executionFlowchartId: derived.executionFlowchartId,
    editorMode: params.editorMode,
    skipFlowchartTypeConfigurator,
  });

  const lineageNodeTaskSchemaIds = useMemo((): Array<string> => {
    const schemas = data?.availableTaskSchemas;
    if (schemas == null || schemas.length === 0) {
      return EMPTY_LINEAGE_NODE_TASK_SCHEMA_IDS;
    }
    return schemas.map((schema) => schema.id);
  }, [data]);

  const lineageProcessId = params.resolution === 'process' ? params.processId : null;
  const lineageResult = useTaskGroupLineageData({
    flowchartId: derived.executionFlowchartId,
    flowchartProcessId: lineageProcessId,
    nodeTaskSchemaIds: lineageNodeTaskSchemaIds,
  });

  const clearDraft = useCallback((): void => {
    setDraftState(null);
  }, []);

  const prevEditorModeRef = useRef(params.editorMode);
  useEffect(() => {
    if (prevEditorModeRef.current !== params.editorMode) {
      prevEditorModeRef.current = params.editorMode;
      setDraftState((prev) => {
        if (prev == null) {
          return prev;
        }
        const updatedNodes = refreshNodeDisplayNames(prev.staticFlowchart.nodesStaticData, params.editorMode);
        if (updatedNodes === prev.staticFlowchart.nodesStaticData) {
          return prev;
        }
        return {
          ...prev,
          staticFlowchart: {...prev.staticFlowchart, nodesStaticData: updatedNodes},
        };
      });
    }
  }, [params.editorMode]);

  useBootstrapExecutionDraft({
    data,
    executionEditorConfiguratorBase: executionEditorConfigurator,
    setDraftState,
    lineageResult,
  });

  const readinessSnapshot: ExecutionFlowchartReadinessSnapshot = useMemo(
    () => ({
      isConfiguratorLoadError,
      dataResult,
      data,
      executionEditorConfigurator,
      externalInaccessible: derived.externalInaccessible,
      externalPending: derived.externalPending,
    }),
    [
      isConfiguratorLoadError,
      dataResult,
      data,
      executionEditorConfigurator,
      derived.externalInaccessible,
      derived.externalPending,
    ]
  );

  return {
    draftState,
    setDraftState,
    clearDraft,
    readinessSnapshot,
  };
}

/** Draft + snapshot when the root entity is a flowchart config (ACM or viewer-by-config). */
export function useExecutionFlowchartDraftStateForConfig(args: {
  flowchartConfigId: string | null;
  editorMode: FlowchartEditorMode;
}): UseExecutionFlowchartDraftStateResult {
  return useExecutionFlowchartDraftState({
    resolution: 'flowchartConfig',
    flowchartConfigId: args.flowchartConfigId,
    editorMode: args.editorMode,
  });
}

/** Draft + snapshot when the root entity is a process (PCM, viewer-by-process, task group viewer). */
export function useExecutionFlowchartDraftStateForProcess(args: {
  processId: string;
  editorMode: FlowchartEditorMode;
}): UseExecutionFlowchartDraftStateResult {
  return useExecutionFlowchartDraftState({
    resolution: 'process',
    processId: args.processId,
    editorMode: args.editorMode,
  });
}
